import {
  Component,
  ChangeDetectorRef,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, throwError } from 'rxjs';
import { delay, map, retry, takeUntil } from 'rxjs/operators';

import {
  AppStore,
  CopyKeyword,
  IKeywordDefinition,
  SelectKeyword,
  SelectedForDeleteAction,
  getFormattedKeyword,
  getSelectedKeyword,
} from '../../../store';
import { CitationService, WkKeywordService } from '../../../services';
import { CommonConstants } from '../../../shared';

@Component({
  selector: 'wk-keyword',
  templateUrl: './wk-keyword.component.html',
  styleUrls: ['./wk-keyword.component.scss'],
  standalone: false,
})
export class WkKeywordComponent implements OnInit {
  public keywords: Observable<IKeywordDefinition[]> =
    this.store.select(getFormattedKeyword);
  @ViewChild('keywordDefinitionReference')
  keywordDefinitionReference: ElementRef;
  showKeywordActionIcons;
  heading: string;
  parser = new DOMParser();
  editorData: Document;

  private readonly onDestroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<AppStore>,
    private readonly citationService: CitationService,
    private readonly keywordService: WkKeywordService,
    private readonly elementRef: ElementRef,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.showKeywordActionIcons = this.citationService.showCitationActionIcons;
    this.scrollKeywordOnViewport();
    this.heading = this.keywordService.getCurrentSite().keyword?.heading;
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  public chkForChevrons(keyword: IKeywordDefinition): Element | undefined {
    const editorData = this.parser.parseFromString(
      this.citationService.ckeditorInstanceFromEditorComp?.getData(),
      'text/html'
    );
    return editorData.querySelector(`[id='${keyword.preferedLabelId}']`);
  }

  private scrollKeywordOnViewport(): void {
    const values = [
      this.citationService.changePanel$,
      this.store.select(getSelectedKeyword),
    ];
    combineLatest(values)
      .pipe(
        takeUntil(this.onDestroy$),
        map((value: [number, string]) => {
          if (
            !this.keywordDefinitionReference?.nativeElement.querySelector(
              `[id='${value[1]}']`
            )
          ) {
            return throwError(() => new Error('Error!'));
          }
          return value;
        }),
        retry({ delay: (error) => error.pipe(delay(1000), retry(5)) })
      )
      .subscribe((value: [number, string]) => {
        if (value[0] === 1 && value[1] && typeof value[1] === 'string') {
          delay(1000);
          this.keywordDefinitionReference.nativeElement
            .querySelector(`[id='${value[1]}']`)
            .scrollIntoView(true);
        }
      });
  }

  public trackByKeyWord(_: number, keyword: IKeywordDefinition): string {
    return keyword.id;
  }

  public onSelectKeyword(keyword: IKeywordDefinition): void {
    if (!this.keywordService.toggledChevron.value) {
      this.store.dispatch(new SelectKeyword(keyword));
    }
    this.keywordService.toggledChevron.next(false);
  }

  public onEditKeyword(event: Event, keyword: IKeywordDefinition): void {
    event.stopPropagation();
    this.keywordService.editKeyword.emit(keyword);
  }

  /**
   * This is when the trash icon in RHP is clicked
   * @param {Event} evt
   * @param {IKeywordDefinition} keyword
   */
  public onDeleteKeyword(evt: Event, keyword: IKeywordDefinition) {
    evt.stopPropagation();
    this.store.dispatch(new SelectedForDeleteAction(undefined));
    this.keywordService.deleteKeyword(keyword, true);
  }

  public onDefinitionClick(event): void {
    const {
      pciCoreWebResource,
      pciCoreBibliographicResource,
      pciCoreMediaResource,
      typeOfAttribute,
    } = CommonConstants;
    const typeOf = event.target.getAttribute(typeOfAttribute);
    if (
      typeOf === pciCoreWebResource ||
      typeOf === pciCoreBibliographicResource ||
      typeOf === pciCoreMediaResource
    ) {
      this.citationService.routeToLink(this.elementRef, event);
    }
  }

  public onCopyKeyword(event: Event, keyword: IKeywordDefinition): void {
    event.stopPropagation();
    const { data, model } = this.citationService.ckeditorInstanceFromEditorComp;
    const selectedContent = data.stringify(
      model.getSelectedContent(model.document.selection)
    );
    if (selectedContent) {
      this.store.dispatch(new CopyKeyword({ isCopy: true, keyword }));
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  toggleChevronNav(keyword: IKeywordDefinition, direction: string) {
    this.keywordService.toggledChevron.next(true);
    this.keywordService.chevronNav.next({
      id: keyword.id,
      navDirection: direction,
    });
  }
}
